import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StarRating from '../UI/StarRating';
import './TrainersSection.css';

interface Trainer {
  id: number;
  profile_picture: string;
  full_name: string;
  sub_category: string;
  rating: { average_rating: number; review_count: number };
  premium: boolean;
  remote: boolean;
  lowest_service_price: number;
  location: string;
}

interface TrainersSectionProps {
  trainers: Trainer[];
  location: string;
  category: string;
  showTrainers?: boolean;
  trainersTitle?: string;
  trainersSubtitle?: string;
  trainersLimit?: number;
  trainersButtonText?: string;
  trainersSeeAllText?: string;
  trainersCountText?: string;
  trainersBadgeText?: string;
  trainersStat1Number?: string;
  trainersStat1Label?: string;
  trainersStat2Number?: string;
  trainersStat2Label?: string;
  trainersStat3Number?: string;
  trainersStat3Label?: string;
  trainersHighlightText?: string;
  trainersCtaSubtitle?: string;
}

const TrainersSection: React.FC<TrainersSectionProps> = ({
  trainers,
  location,
  category,
  showTrainers = true,
  trainersTitle = "Odkryj swoich trenerów",
  trainersSubtitle = `Poznaj najlepszych trenerów w ${location} specjalizujących się w ${category}`,
  trainersLimit = 8,
  trainersButtonText = "Zarejestruj się, aby zobaczyć więcej trenerów",
  trainersSeeAllText = "Zobacz wszystkich trenerów",
  trainersCountText,
  trainersBadgeText = `Top trenerzy w ${location}`,
  trainersStat1Number = "8+",
  trainersStat1Label = "Ekspertów",
  trainersStat2Number = "4.8★",
  trainersStat2Label = "Średnia ocena",
  trainersStat3Number = "100%",
  trainersStat3Label = "Weryfikacja",
  trainersHighlightText = `w ${location}`,
  trainersCtaSubtitle = "Dołącz do setek klientów, którzy już zmienili swoje życie"
}) => {
  const [showAll, setShowAll] = useState(false);

  if (!showTrainers || !trainers || trainers.length === 0) {
    return null; // Don't render section if disabled or no trainers
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="landing-trainers-header">
          <div className="landing-trainers-header-badge">
            <svg className="landing-trainers-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{trainersBadgeText}</span>
          </div>
          <h2 className="landing-trainers-header-title">
            {trainersTitle}
            <span className="landing-trainers-header-highlight">{trainersHighlightText}</span>
          </h2>
          <p className="landing-trainers-header-subtitle">
            {trainersSubtitle}
          </p>
          <div className="landing-trainers-header-stats">
            <div className="landing-trainers-header-stat">
              <div className="landing-trainers-header-stat-number">{trainersStat1Number}</div>
              <div className="landing-trainers-header-stat-label">{trainersStat1Label}</div>
            </div>
            <div className="landing-trainers-header-stat">
              <div className="landing-trainers-header-stat-number">{trainersStat2Number}</div>
              <div className="landing-trainers-header-stat-label">{trainersStat2Label}</div>
            </div>
            <div className="landing-trainers-header-stat">
              <div className="landing-trainers-header-stat-number">{trainersStat3Number}</div>
              <div className="landing-trainers-header-stat-label">{trainersStat3Label}</div>
            </div>
          </div>
        </div>

        <div className="landing-trainers-grid">
          {trainers.slice(0, showAll ? trainers.length : trainersLimit).map((trainer) => (
            <div
              key={trainer.id}
              className="landing-trainer-card"
              style={{
                borderRadius: '1rem',
                overflow: 'hidden',
                backgroundColor: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                maxWidth: 'none',
                margin: '0',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
            >
              <div className="landing-trainer-image-container" style={{
                width: '100%',
                paddingBottom: '133.33%', // 3:4 ratio (4/3 = 1.3333 = 133.33%)
                backgroundColor: '#f3f4f6',
                position: 'relative',
                overflow: 'hidden',
                height: 0
              }}>
                <img
                  src={trainer.profile_picture || '/images/default-avatar.png'}
                  alt={trainer.full_name}
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                  loading="lazy"
                />
                {trainer.premium && (
                  <div style={{
                    position: 'absolute',
                    top: '0.75rem',
                    right: '0.75rem',
                    backgroundColor: '#fbbf24',
                    color: '#111827',
                    fontSize: '1rem',
                    padding: '0.5rem 0.875rem',
                    borderRadius: '9999px',
                    fontWeight: '700',
                    zIndex: 2,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    Wyróżnione
                  </div>
                )}
                {trainer.remote && (
                  <div style={{
                    position: 'absolute',
                    top: '0.75rem',
                    left: '0.75rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    fontSize: '1rem',
                    padding: '0.5rem 0.875rem',
                    borderRadius: '9999px',
                    fontWeight: '700',
                    zIndex: 2,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    Online
                  </div>
                )}
              </div>

              <div style={{ padding: '1.5rem 1rem', textAlign: 'center' }}>
                <h4 style={{
                  color: '#111827',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  margin: '0',
                  lineHeight: '1.3',
                  textAlign: 'center'
                }}>
                  {trainer.full_name}
                </h4>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  <p style={{
                    color: '#6b7280',
                    fontSize: '1.25rem',
                    margin: '0',
                    fontWeight: '500',
                    textAlign: 'center'
                  }}>
                    {trainer.sub_category}
                  </p>
                  <p style={{
                    color: '#6b7280',
                    fontSize: '1.125rem',
                    margin: '0',
                    fontWeight: '500',
                    textAlign: 'center'
                  }}>
                    📍 {trainer.location}
                  </p>
                </div>

                <div style={{
                  marginTop: '1.25rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  width: '100%'
                }}>
                  <StarRating
                    rating={trainer.rating.average_rating}
                    size="large"
                    readonly
                  />
                  <span style={{
                    color: '#6b7280',
                    fontSize: '1.125rem',
                    fontWeight: '500'
                  }}>
                    ({trainer.rating.review_count} opinii)
                  </span>
                </div>

                <div style={{
                  marginTop: '1.25rem',
                  marginBottom: '1.5rem',
                  padding: '1rem 1.25rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'baseline',
                  gap: '0.5rem'
                }}>
                  <p style={{
                    color: '#1e293b',
                    fontSize: '1.375rem',
                    fontWeight: '700',
                    margin: '0',
                    textAlign: 'center'
                  }}>
                    {trainer.lowest_service_price > 0 ? `${trainer.lowest_service_price} zł` : 'Stawka indywidualna'}
                  </p>
                  <p style={{
                    color: '#64748b',
                    fontSize: '1rem',
                    margin: '0',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    / sesja
                  </p>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Link
                    to={`/ads/${trainer.id}`}
                    className="landing-trainer-btn"
                    style={{
                      width: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0.5rem',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '1rem 1.5rem',
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#2563eb';
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#3b82f6';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    Zobacz profil
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <section className="landing-trainers-cta">
            <div className="landing-trainers-cta-container">
              {!showAll && (
                <>
                  <h2 className="landing-trainers-cta-title">
                    {trainersCountText ? (
                      <span dangerouslySetInnerHTML={{ __html: trainersCountText }} />
                    ) : (
                      <>
                        Poznaj <span className="text-yellow-300">{Math.max(0, trainers.length - trainersLimit)}+</span> więcej trenerów w {location}
                      </>
                    )}
                  </h2>
                  <p className="landing-trainers-cta-subtitle">
                    {trainersCtaSubtitle}
                  </p>
                  <Link
                    to="/register?type=client"
                    className="landing-trainers-cta-button"
                  >
                    {trainersButtonText}
                    <svg className="landing-trainers-cta-button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                </>
              )}
              <Link
                to="/search?trainerAds=true"
                className="landing-trainers-see-all"
              >
                {trainersSeeAllText}
                <svg className="landing-trainers-see-all-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default TrainersSection;